import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Typography } from '@deeptrue-frontend/ui';
import { useFormatMessage } from 'components/IntlProvider';
import { getTranslation, LocaleType } from '../utils/getTranslations';

export interface TitleType {
  en: string;
  de?: string;
  fr?: string;
  it?: string;
}

interface ChartWrapperProps {
  title?: TitleType | string;
  subtitle?: string | null;
  keyMessage?: any;
  isCollapsable?: boolean;
  mainChart?: JSX.Element | null;
  secondaryChart?: JSX.Element | null;
  locale: LocaleType;
}

const Wrapper = styled.div`
  width: 100%;
  max-width: 60rem;
  display: flex;
  flex-direction: column;
  padding: 2rem;
  border: 1px solid #e3e5e8;
  box-shadow: 0px 2px 4px rgba(63, 63, 68, 0.041958),
    0px 1px 1px rgba(63, 63, 68, 0.0769231);
  border-radius: 4px;
  background-color: white;
`;
const StyledButton = styled.button`
  background-color: transparent;
  border: none;
  width: fit-content;
  cursor: pointer;
  display: block !important;
  margin-top: 2.25rem;
`;
const Line = styled.div<{
  isCollapsable: boolean | undefined;
  hasSecondaryChart: boolean;
}>`
  width: 100%;
  height: 2px;
  display: ${({ hasSecondaryChart }) => (hasSecondaryChart ? 'block' : 'none')};
  background-color: ${({ isCollapsable }) =>
    isCollapsable ? '#e3e5e8' : 'transparent'};
  margin: ${({ hasSecondaryChart, isCollapsable }) =>
    hasSecondaryChart && isCollapsable
      ? '2rem 0'
      : hasSecondaryChart
      ? '1rem 0'
      : 0};
`;

const ChartsWrapper = ({
  title,
  subtitle,
  keyMessage,
  mainChart,
  secondaryChart,
  isCollapsable,
  locale,
}: ChartWrapperProps) => {
  const [isSecondChartVisible, setSecondChartVisible] = useState(
    !isCollapsable
  );
  const hasSecondaryChart = !!secondaryChart;

  const t = useFormatMessage();

  return (
    <Wrapper>
      {title && (
        <Typography
          mb={'1.5rem'}
          fontWeight="500"
          fontSize="1rem"
          lineHeight="1.5rem"
        >
          {getTranslation(title, locale)}
        </Typography>
      )}
      {subtitle && (
        <Typography
          color="#253858"
          fontWeight="400"
          fontSize="0.875rem"
          lineHeight="1.25rem"
        >
          {t(
            'reports.activation.subtitle',
            'Based on deeptrue’s psychological model'
          )}
        </Typography>
      )}
      {keyMessage && (
        <Typography
          fontWeight="500"
          fontSize="1rem"
          lineHeight="1.5rem"
          marginBottom="1.5rem"
          marginTop="-0.5rem"
        >
          {`'${getTranslation(keyMessage, locale)}'`}
        </Typography>
      )}
      {mainChart}
      {isSecondChartVisible ||
        (mainChart?.type.name === 'OpenQuestion' &&
          secondaryChart?.type.name === 'OpenQuestion' && (
            <Line
              isCollapsable={isCollapsable}
              hasSecondaryChart={hasSecondaryChart}
            />
          ))}
      {isSecondChartVisible && secondaryChart}
      {isCollapsable && hasSecondaryChart && (
        <StyledButton
          onClick={() => setSecondChartVisible(!isSecondChartVisible)}
        >
          <Typography fontSize={'0.875rem'} fontWeight={600} color={'#144BB8'}>
            {isSecondChartVisible
              ? t('reports.chartsWrapper.hideDetails', 'Hide details')
              : t('reports.chartsWrapper.showDetails', 'Show details')}
          </Typography>
        </StyledButton>
      )}
    </Wrapper>
  );
};

export default ChartsWrapper;
